import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CButton } from "@coreui/react";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import { CDataTable } from "@tradingdroid/frontends-components";
import type { CDataTableColumn } from "@tradingdroid/frontends-components";

import { WithdrawAddressCell } from "./WithdrawAddressCell";
import { WithdrawAddressFormModal } from "./WithdrawAddressFormModal";

export const WithdrawAddressesSection: React.FC = () => {
  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();

  const [addresses, setAddresses] = useState<T.TDT.ApiWithdrawAddress[]>([]);
  const [networks, setNetworks] = useState<T.TDT.ApiWithdrawNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const loadAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const [addressesRes, networksRes] = await Promise.all([
        tdApi.getWithdrawAddresses(),
        tdApi.getWithdrawNetworks(),
      ]);
      setAddresses(addressesRes?.items ?? []);
      setNetworks(networksRes?.items ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast({ message: msg, type: "danger", title: "Failed to load addresses" });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void loadAddresses();
  }, [loadAddresses]);

  const handleCreated = useCallback((created: T.TDT.ApiWithdrawAddress) => {
    setAddresses((prev) => [...prev, created]);
  }, []);

  const handleEditLabel = useCallback(
    async (addr: T.TDT.ApiWithdrawAddress) => {
      const { confirmed, promptValue } = await confirmModal(`Rename address '${addr.label}'`, {
        title: "Edit Label",
        confirmLabel: "Save",
        confirmColor: "primary",
        promptConfig: {
          type: "text",
          label: "Label",
          placeholder: "e.g. Cold wallet",
          defaultValue: addr.label,
        },
      });
      if (!confirmed) return;
      const newLabel = (promptValue ?? "").trim();
      if (!newLabel || newLabel === addr.label) return;

      try {
        const updated = await tdApi.updateWithdrawAddress(addr.id, { label: newLabel });
        if (updated) {
          setAddresses((prev) => prev.map((a) => (a.id === addr.id ? updated : a)));
          showToast({ message: `Label updated to '${newLabel}'`, type: "success" });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast({ message: msg, type: "danger", title: "Failed to update label" });
      }
    },
    [confirmModal, showToast],
  );

  const handleDelete = useCallback(
    async (addr: T.TDT.ApiWithdrawAddress) => {
      const { confirmed } = await confirmModal(
        `Delete address '${addr.label}' on ${addr.network_code}?`,
        { title: "Delete Address", confirmLabel: "Delete", confirmColor: "danger" },
      );
      if (!confirmed) return;

      try {
        await tdApi.deleteWithdrawAddress(addr.id);
        setAddresses((prev) => prev.filter((a) => a.id !== addr.id));
        showToast({ message: `Address '${addr.label}' deleted`, type: "success" });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast({ message: msg, type: "danger", title: "Failed to delete address" });
      }
    },
    [confirmModal, showToast],
  );

  const columns = useMemo<CDataTableColumn<T.TDT.ApiWithdrawAddress>[]>(
    () => [
      {
        columnKey: "id",
        columnLabel: "ID",
        columnStyle: { width: "70px" },
      },
      {
        columnKey: "label",
        columnLabel: "Label",
      },
      {
        columnKey: "network_code",
        columnLabel: "Network",
        columnStyle: { width: "110px" },
      },
      {
        columnKey: "address",
        columnLabel: "Address",
        cellRender: (addr) => <WithdrawAddressCell address={addr.address} />,
      },
      {
        columnKey: "actions",
        columnLabel: "Actions",
        columnStyle: { width: "120px" },
        cellRender: (addr) => (
          <div className="d-flex gap-2">
            <CButton
              color="secondary"
              size="sm"
              title="Edit label"
              aria-label="Edit label"
              onClick={() => handleEditLabel(addr)}
            >
              ✏️
            </CButton>
            <CButton
              color="danger"
              size="sm"
              title="Delete"
              aria-label="Delete"
              onClick={() => handleDelete(addr)}
            >
              🗑
            </CButton>
          </div>
        ),
      },
    ],
    [handleEditLabel, handleDelete],
  );

  return (
    <section className="WithdrawAddressesSection">
      <div className="WithdrawAddressesSection-header d-flex justify-content-between align-items-center mb-3">
        <h2 className="h5 mb-0">Withdrawal Addresses</h2>
        <CButton color="success" size="sm" onClick={() => setShowAddModal(true)}>
          + Add Address
        </CButton>
      </div>

      <CDataTable columns={columns} items={addresses} loading={loading} hover striped />

      <WithdrawAddressFormModal
        visible={showAddModal}
        networks={networks}
        onClose={() => setShowAddModal(false)}
        onCreated={handleCreated}
      />
    </section>
  );
};
